<template>
    <div>
        <!-- Mobile dropdown -->
        <div class="lg:hidden relative mb-4" ref="dropdown">
            <button
                @click="open = !open"
                type="button"
                class="flex items-center justify-between w-full rounded border bg-white px-5 py-3 text-sm font-semibold text-gray-900"
            >
                <span>{{ activeLabel }}</span>
                <svg class="h-4 w-4 text-gray-500 transition-transform" :class="{ 'rotate-180': open }" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                </svg>
            </button>
            <div v-show="open" class="absolute z-20 mt-1 w-full rounded border bg-white shadow-lg py-1">
                <a
                    v-for="tab in tabs"
                    :key="tab.href"
                    :href="tab.href"
                    class="flex items-center justify-between px-4 py-2.5 text-sm transition-colors"
                    :class="tab.active ? 'text-teal-600 font-semibold bg-gray-50' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'"
                >
                    <span>{{ tab.label }}</span>
                    <svg v-if="tab.active" class="h-4 w-4 text-teal-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                    </svg>
                </a>
            </div>
        </div>

        <!-- Desktop tabs -->
        <div class="hidden lg:block -mb-px">
            <nav
                :class="[
                    'flex rounded-t relative z-10',
                    customClass,
                    border
                ]"
                aria-label="Tabs">
                <slot />
            </nav>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        name: {},
        border: {
            type: String,
            default: 'border-b'
        },
        customClass: {
            type: String,
            default: 'bg-white'
        }
    },
    data() {
        return {
            open: false,
            tabs: [],
        }
    },
    computed: {
        activeLabel() {
            const active = this.tabs.find(t => t.active);
            return active ? active.label : '';
        }
    },
    mounted() {
        this.extractTabs();
        document.addEventListener('click', this.handleClickOutside);
    },
    updated() {
        this.extractTabs();
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        extractTabs() {
            if (!this.$slots.default) return;

            const vnodes = this.$slots.default();
            const next = this.flattenVNodes(vnodes)
                .filter(vnode => vnode.props)
                .map(vnode => ({
                    href: vnode.props.href || '',
                    active: vnode.props.active === true || vnode.props.active === '',
                    label: (vnode.props.label || this.extractLabel(vnode)).trim(),
                }))
                .filter(tab => tab.href && tab.label);

            if (this.sameTabs(next, this.tabs)) return;
            this.tabs = next;
        },
        sameTabs(a, b) {
            if (a.length !== b.length) return false;
            for (let i = 0; i < a.length; i++) {
                if (a[i].href !== b[i].href || a[i].active !== b[i].active || a[i].label !== b[i].label) {
                    return false;
                }
            }
            return true;
        },
        flattenVNodes(vnodes) {
            const result = [];
            for (const vnode of vnodes) {
                if (typeof vnode.type === 'symbol') {
                    if (Array.isArray(vnode.children)) {
                        result.push(...this.flattenVNodes(vnode.children));
                    }
                } else {
                    result.push(vnode);
                }
            }
            return result;
        },
        extractLabel(vnode) {
            if (vnode.children) {
                if (typeof vnode.children === 'string') return vnode.children;
                if (vnode.children.default) {
                    const content = vnode.children.default();
                    return content.map(c => {
                        if (typeof c === 'string') return c;
                        if (c.children && typeof c.children === 'string') return c.children;
                        return '';
                    }).join('');
                }
            }
            return '';
        },
        handleClickOutside(e) {
            if (this.$refs.dropdown && !this.$refs.dropdown.contains(e.target)) {
                this.open = false;
            }
        }
    }
}
</script>
